import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { CheckCircleIcon, CloudArrowUpIcon, DocumentIcon, XMarkIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

const REQUIRED_DOCS = [
  { value: 'Business Tax Return 2025', label: 'Business Tax Return 2025' },
  { value: 'Business Tax Return 2024', label: 'Business Tax Return 2024' },
  { value: 'Business Tax Return 2023', label: 'Business Tax Return 2023' },
  { value: 'Interim P&L 2026 YTD', label: 'Interim P&L 2026 (YTD)' },
  { value: 'Trailing 12 Months P&L', label: 'Trailing 12 Months P&L' },
  { value: 'Balance Sheet', label: 'Balance Sheet' },
  { value: 'LOI', label: 'Letter of Intent (LOI)' },
];

const EXTRA_DOCS = [
  { value: 'CIM', label: 'Confidential Information Memorandum (CIM)' },
  { value: 'Debt Schedule', label: 'Debt Schedule' },
  { value: 'AR Aging', label: 'AR Aging' },
  { value: 'AP Aging', label: 'AP Aging' },
  { value: 'Bank Statements', label: 'Bank Statements' },
  { value: 'Articles of Organization', label: 'Articles of Organization' },
  { value: 'Operating Agreement', label: 'Operating Agreement' },
  { value: 'Business Valuation', label: 'Business Valuation' },
  { value: 'Lease Agreement', label: 'Lease Agreement' },
  { value: 'Franchise Agreement', label: 'Franchise Agreement' },
];

// Conditional docs based on flags set on the business
const REAL_ESTATE_DOCS = [{ value: 'Real Estate Valuation', label: 'Real Estate Valuation / Appraisal' }];
const LICENSE_DOCS = [{ value: 'Business License', label: 'Business License' }];
const ADDBACKS_DOCS = [{ value: 'Addbacks Schedule', label: 'Addbacks Schedule' }];
const CUSTOMER_CONC_DOCS = [{ value: 'Top 10 Customer List', label: 'Top 10 Customer List' }];

const guessDocType = (filename) => {
  const n = (filename || '').toLowerCase();
  const yearMatch = n.match(/20(2[2-6])/);
  const year = yearMatch ? `20${yearMatch[1]}` : null;
  if (/p[\s_-]*&?[\s_-]*l|profit[\s_-]*and[\s_-]*loss/.test(n)) {
    if (n.includes('trailing') || n.includes('ttm') || n.includes('t12')) return 'Trailing 12 Months P&L';
    if (year === '2026' || n.includes('ytd') || n.includes('interim')) return 'Interim P&L 2026 YTD';
  }
  if (/(business|biz|corp|1120|1065)[\s_-]*tax|tax[\s_-]*return/.test(n) && year) return `Business Tax Return ${year}`;
  if (/balance[\s_-]*sheet/.test(n)) return 'Balance Sheet';
  if (/cim|memorandum/.test(n)) return 'CIM';
  if (/loi|letter[\s_-]*of[\s_-]*intent/.test(n)) return 'LOI';
  if (/debt[\s_-]*schedule/.test(n)) return 'Debt Schedule';
  if (/articles/.test(n)) return 'Articles of Organization';
  if (/operating[\s_-]*agreement/.test(n)) return 'Operating Agreement';
  if (/\bar[\s_-]*aging\b/.test(n)) return 'AR Aging';
  if (/\bap[\s_-]*aging\b/.test(n)) return 'AP Aging';
  if (/bank[\s_-]*statement/.test(n)) return 'Bank Statements';
  if (/valuation|appraisal/.test(n)) return 'Real Estate Valuation';
  if (/license/.test(n)) return 'Business License';
  if (/addback/.test(n)) return 'Addbacks Schedule';
  return '';
};

export default function BusinessDocsCard({ currentUser, loanApplicationId, businessIndex, businessId, selectedBusiness, refreshKey = 0 }) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [bulkQueue, setBulkQueue] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);

  const activeRequiredDocs = [
    ...REQUIRED_DOCS,
    ...(selectedBusiness?.realEstateIncluded ? REAL_ESTATE_DOCS : []),
    ...(selectedBusiness?.licenseNeeded ? LICENSE_DOCS : []),
    ...(selectedBusiness?.hasOtherAddbacks ? ADDBACKS_DOCS : []),
    ...(selectedBusiness?.topCustomerConcentration ? CUSTOMER_CONC_DOCS : []),
  ];

  const DOC_TYPES = [
    { category: 'Required', items: activeRequiredDocs },
    { category: 'Extra Files', items: EXTRA_DOCS },
  ];

  // Fetch already-uploaded files for this business
  useEffect(() => {
    if (!currentUser || !loanApplicationId) return;
    let cancelled = false;
    const fetchFiles = async () => {
      try {
        const q = query(
          collection(db, 'documentMetadata'),
          where('loanApplicationId', '==', loanApplicationId),
          where('userId', '==', currentUser.uid)
        );
        const snap = await getDocs(q);
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Filter to this business (match businessIndex or businessId)
        const filtered = all.filter(f =>
          (businessIndex != null && f.businessIndex === businessIndex) ||
          (businessId && f.businessId === businessId)
        );
        if (!cancelled) setUploadedFiles(filtered);
      } catch (err) {
        console.error('Error fetching business docs:', err);
      }
    };
    fetchFiles();
    return () => { cancelled = true; };
  }, [currentUser, loanApplicationId, businessIndex, businessId, refreshKey]);

  const uploadedLabels = uploadedFiles.map(f => f.label || f.documentName);
  const requiredUploaded = activeRequiredDocs.filter(d => uploadedLabels.includes(d.value)).length;
  const requiredComplete = requiredUploaded === activeRequiredDocs.length;
  const extraUploaded = EXTRA_DOCS.filter(d => uploadedLabels.includes(d.value)).length;

  const businessPath = businessIndex != null ? `business-${businessIndex}` : 'business';

  const performUpload = (file, label, onProgress) => new Promise((resolve, reject) => {
    const path = `documents/${currentUser.uid}/${loanApplicationId}/${businessPath}/${label}/${file.name}`;
    const sRef = storageRef(storage, path);
    const task = uploadBytesResumable(sRef, file);
    task.on('state_changed',
      (snapshot) => { if (onProgress) onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)); },
      (err) => reject(err),
      async () => {
        try {
          const fileUrl = await getDownloadURL(task.snapshot.ref);
          const metadata = {
            fileName: file.name,
            fileUrl,
            uploadedAt: new Date().toISOString(),
            userId: currentUser.uid,
            documentName: label,
            loanApplicationId,
            ownerId: null,
            label,
            businessIndex: businessIndex != null ? businessIndex : null,
            businessId: businessId || null,
            storagePath: path,
          };
          const docRef = await addDoc(collection(db, 'documentMetadata'), metadata);
          resolve({ id: docRef.id, ...metadata });
        } catch (err) { reject(err); }
      }
    );
  });

  const handleUpload = async () => {
    if (!selectedFile) return;
    const label = (customLabel && customLabel.trim()) || (selectedDocType === 'Other' ? '' : selectedDocType);
    if (!label) return;
    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccessMessage('');
    try {
      const saved = await performUpload(selectedFile, label, setUploadProgress);
      setUploadedFiles(prev => [...prev, saved]);
      setSuccessMessage(`${label} uploaded successfully!`);
      setSelectedFile(null);
      setSelectedDocType('');
      setCustomLabel('');
      const fileInput = document.getElementById(`biz-file-upload-${businessIndex ?? 'x'}`);
      if (fileInput) fileInput.value = '';
    } catch (err) {
      setError('Upload failed: ' + (err.message || 'unknown error'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemove = async (file) => {
    if (!window.confirm(`Remove ${file.fileName}?`)) return;
    try {
      if (file.storagePath) {
        try { await deleteObject(storageRef(storage, file.storagePath)); } catch (e) { /* may already be gone */ }
      }
      await deleteDoc(doc(db, 'documentMetadata', file.id));
      setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
    } catch (err) {
      console.error('Error removing file:', err);
      alert('Error removing file');
    }
  };

  // ---- Bulk upload ----
  const handleBulkFilesPicked = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBulkQueue(Array.from(fileList).map(file => ({
      file,
      label: guessDocType(file.name),
      customLabel: '',
      status: 'pending',
      error: '',
    })));
  };
  const updateBulkRow = (idx, patch) => setBulkQueue(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeBulkRow = (idx) => setBulkQueue(prev => prev.filter((_, i) => i !== idx));
  const startBulkUpload = async () => {
    setBulkUploading(true);
    for (let i = 0; i < bulkQueue.length; i++) {
      const row = bulkQueue[i];
      if (row.status === 'done') continue;
      const label = (row.customLabel && row.customLabel.trim()) || row.label;
      if (!label) { updateBulkRow(i, { status: 'error', error: 'Pick a doc type or give it a custom name' }); continue; }
      updateBulkRow(i, { status: 'uploading', error: '' });
      try {
        const saved = await performUpload(row.file, label);
        setUploadedFiles(prev => [...prev, saved]);
        updateBulkRow(i, { status: 'done' });
      } catch (err) {
        updateBulkRow(i, { status: 'error', error: err.message || 'Upload failed' });
      }
    }
    setBulkUploading(false);
  };
  const closeBulkModal = () => { if (!bulkUploading) setBulkQueue([]); };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" />
        <h2 className="text-xl font-semibold text-gray-900">Business Documents</h2>
        {selectedBusiness?.businessName && <span className="text-sm text-gray-500">— {selectedBusiness.businessName}</span>}
      </div>
      <p className="text-sm text-gray-500">Upload your business documents here. We'll let you know which ones we still need.</p>

      {/* Bulk upload CTA */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Have a bunch of files?</h3>
          <p className="text-xs text-gray-500 mt-0.5">Upload multiple files or a whole folder — we'll let you tag each one.</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
            Upload files
            <input type="file" multiple className="hidden"
              onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
          </label>
          <label className="cursor-pointer text-xs font-medium px-3 py-2 rounded-lg bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50">
            Upload folder
            <input type="file" multiple webkitdirectory="" directory="" className="hidden"
              onChange={(e) => { handleBulkFilesPicked(e.target.files); e.target.value = ''; }} />
          </label>
        </div>
      </div>

      {/* Progress checklist */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Required Business Documents</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              requiredComplete ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
            }`}>
              {requiredUploaded} / {activeRequiredDocs.length}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activeRequiredDocs.map(d => (
              <div key={d.value} className="flex items-center gap-2 text-xs">
                {uploadedLabels.includes(d.value) ? (
                  <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-orange-300 flex-shrink-0" />
                )}
                <span className={uploadedLabels.includes(d.value) ? 'text-green-700' : 'text-gray-600'}>{d.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Extra Files</h3>
            {extraUploaded > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">
                {extraUploaded} uploaded
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {EXTRA_DOCS.map(d => (
              <div key={d.value} className="flex items-center gap-2 text-xs">
                {uploadedLabels.includes(d.value) ? (
                  <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-gray-200 flex-shrink-0" />
                )}
                <span className={uploadedLabels.includes(d.value) ? 'text-green-700' : 'text-gray-400'}>{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upload form */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Upload a Document</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
            <select
              value={selectedDocType}
              onChange={(e) => setSelectedDocType(e.target.value)}
              disabled={!!customLabel}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
            >
              <option value="">Select document type...</option>
              {DOC_TYPES.map(cat => (
                <optgroup key={cat.category} label={cat.category}>
                  {cat.items.map(item => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Or use a custom name (overrides selection)</label>
            <input type="text" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="e.g. Franchise Disclosure Document"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
            <input
              id={`biz-file-upload-${businessIndex ?? 'x'}`}
              type="file"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
          </div>

          {uploading && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-indigo-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
          {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}

          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading || !selectedFile || (!selectedDocType && !customLabel.trim())}
            className="w-full py-2.5 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <CloudArrowUpIcon className="h-5 w-5" />
            {uploading ? `Uploading... ${uploadProgress}%` : 'Upload Document'}
          </button>
        </div>

        {/* Uploaded files list */}
        {uploadedFiles.length > 0 && (
          <div className="mt-6 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Uploaded files ({uploadedFiles.length})</h4>
            <div className="space-y-1">
              {uploadedFiles.map((f) => (
                <div key={f.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-3 py-2 border border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="font-medium text-gray-700 truncate">{f.label || f.documentName}</span>
                    <span className="text-gray-400 truncate">— {f.fileName}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {f.fileUrl && (
                      <a href={f.fileUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">View</a>
                    )}
                    <button onClick={() => handleRemove(f)} className="text-gray-400 hover:text-red-500" title="Remove">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bulk upload modal */}
      {bulkQueue.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Tag {bulkQueue.length} {bulkQueue.length === 1 ? 'file' : 'files'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">We tried to guess each doc type from the filename — fix any that look wrong.</p>
              </div>
              <button onClick={closeBulkModal} disabled={bulkUploading} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {bulkQueue.map((row, idx) => (
                <div key={idx} className={`border rounded-lg p-3 ${
                  row.status === 'done' ? 'border-green-200 bg-green-50' :
                  row.status === 'error' ? 'border-red-200 bg-red-50' :
                  row.status === 'uploading' ? 'border-indigo-200 bg-indigo-50' :
                  'border-gray-200'
                }`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <DocumentIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate">{row.file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{(row.file.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {row.status === 'done' && <CheckCircleIcon className="h-5 w-5 text-green-500" />}
                      {row.status === 'uploading' && <span className="text-xs text-indigo-600 animate-pulse">Uploading…</span>}
                      {row.status !== 'done' && row.status !== 'uploading' && (
                        <button onClick={() => removeBulkRow(idx)} disabled={bulkUploading} className="text-gray-400 hover:text-red-500 disabled:opacity-30">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <select
                    value={row.label}
                    onChange={(e) => updateBulkRow(idx, { label: e.target.value })}
                    disabled={bulkUploading || row.status === 'done' || !!row.customLabel}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                  >
                    <option value="">Select document type…</option>
                    {DOC_TYPES.map(cat => (
                      <optgroup key={cat.category} label={cat.category}>
                        {cat.items.map(item => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={row.customLabel}
                    onChange={(e) => updateBulkRow(idx, { customLabel: e.target.value })}
                    placeholder="…or type a custom name (overrides above)"
                    disabled={bulkUploading || row.status === 'done'}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                  />
                  {row.error && <p className="mt-2 text-xs text-red-600">{row.error}</p>}
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {bulkQueue.filter(r => r.status === 'done').length} of {bulkQueue.length} uploaded
              </p>
              <div className="flex gap-2">
                <button onClick={closeBulkModal} disabled={bulkUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {bulkQueue.every(r => r.status === 'done') ? 'Done' : 'Cancel'}
                </button>
                <button onClick={startBulkUpload}
                  disabled={bulkUploading || bulkQueue.every(r => r.status === 'done')}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {bulkUploading ? 'Uploading…' : 'Upload all'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
